import { CheckIcon, ChevronDownIcon } from "@storytell/bsky-icons";
import { ChatMessages } from "@storytell/cp-client";
import {
  type StreamingChatId,
  StreamingChatStatus,
  type StreamingOperation,
  isEndTurnOperationId,
} from "@storytell/streaming";
import { TbAlertCircle, TbBulb, TbFileSearch, TbGlobe, TbPhoto, TbRoute } from "solid-icons/tb";
import { type Component, Match, Switch } from "solid-js";
import { ProcessingSpinner } from "~/components/ui";
import { useWire } from "~/wire";

interface OperationHeaderProps {
  operation: StreamingOperation;
  title: string;
  isExpanded: boolean;
  isProcessing: boolean;
  hasBlocks: boolean;
  onToggle: () => void;
  chatId: StreamingChatId;
  isPlanning: boolean;
}

export const OperationHeader: Component<OperationHeaderProps> = (props) => {
  const wire = useWire();

  const getOperationIcon = (operation: StreamingOperation) => {
    // Show spinner when:
    // - Operation is not complete OR
    // - Operation status indicates it's still running
    const isStreaming =
      !operation.isComplete ||
      operation.status === ChatMessages.OperationStatus.RUNNING ||
      operation.status === ChatMessages.OperationStatus.STARTING ||
      operation.status === ChatMessages.OperationStatus.RETRYING;

    let iconElement;
    let backgroundClass = "bg-dark-icon text-primary-text";

    if (isStreaming) {
      iconElement = <ProcessingSpinner class="border-[1.5px] border-primary-text border-t-transparent" />;
    } else if (
      operation.status === ChatMessages.OperationStatus.FAILED ||
      operation.status === ChatMessages.OperationStatus.CANCELLED
    ) {
      iconElement = <TbAlertCircle class="w-3 h-3" />;
      backgroundClass = "bg-red-600 dark:bg-red-500 text-white";
    } else if (props.isPlanning) {
      iconElement = <TbRoute class="w-3 h-3" />;
      backgroundClass = "bg-violet-600 dark:bg-violet-500 text-white";
    } else if (operation.activity === "web_search" || operation.activity === "web_search_v1") {
      // Use globe icon for web search operations
      iconElement = <TbGlobe class="w-3 h-3" />;
      backgroundClass = "bg-green-600 dark:bg-green-500 text-white";
    } else if (operation.activity === "user_kb_search_v1" || operation.activity === "kb_search") {
      // Use file search icon for knowledge base search
      iconElement = <TbFileSearch class="w-3 h-3" />;
      backgroundClass = "bg-blue-600 dark:bg-blue-500 text-white";
    } else if (operation.activity === "read_concepts") {
      // Use lightbulb icon for concept reading
      iconElement = <TbBulb class="w-3 h-3" />;
      backgroundClass = "bg-amber-600 dark:bg-amber-500 text-white";
    } else if (operation.activity === "image_generation") {
      // Use photo icon for image generation
      iconElement = <TbPhoto class="w-3 h-3" />;
      backgroundClass = "bg-orange-600 dark:bg-orange-500 text-white";
    } else {
      iconElement = <CheckIcon size={16} class="stroke-3 w-3 h-3" />;
    }

    // Wrap icon in a circle
    return (
      <div
        class={`w-5 h-5 rounded-full relative top-[6px] left-[7px] flex items-center justify-center ${backgroundClass}`}
      >
        {iconElement}
      </div>
    );
  };
  return (
    <div
      data-renderer="operation-header"
      class="flex items-start justify-between mb-4"
      data-operation={props.operation.operationId}
    >
      {/* Main content: icon, title, expand/collapse */}
      <div class="flex items-start flex-1 gap-4 min-w-0">
        <div class="flex-shrink-0 -mt-0.5">{getOperationIcon(props.operation)}</div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 group">
            <Switch>
              <Match
                when={
                  !isEndTurnOperationId(props.operation.operationId) ||
                  (isEndTurnOperationId(props.operation.operationId) &&
                    wire.stream.chat.getStatus(props.chatId) === StreamingChatStatus.IDLE)
                }
              >
                <button
                  type="button"
                  onClick={props.onToggle}
                  class="text-base pt-0.5 font-medium text-primary-text transition-colors text-left"
                >
                  {props.title}
                </button>
                <button
                  type="button"
                  onClick={props.onToggle}
                  class="flex-shrink-0 p-1 pt-2 hover:bg-canvas-hover rounded transition-colors"
                  title={props.isExpanded ? "Collapse operation" : "Expand operation"}
                >
                  <ChevronDownIcon
                    size={12}
                    class="stroke-3 text-muted-text transition-transform duration-100"
                    classList={{
                      "-rotate-90": !props.isExpanded,
                    }}
                  />
                </button>
              </Match>
              <Match when={isEndTurnOperationId(props.operation.operationId)}>
                <div class="text-base pt-1 font-medium text-primary-text hover:text-secondary-text transition-colors text-left">
                  {props.operation.status === ChatMessages.OperationStatus.COMPLETED ? "Done" : "Processing..."}
                </div>
              </Match>
            </Switch>
          </div>
        </div>
      </div>
    </div>
  );
};
